import { defaultInstallmentEnabled } from "../../../constants/paymentConstants";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
    fetchGroups,
    deleteGroup,
    saveGroup,
    fetchActivePosByCompany,
    setSubscriptionGroup
} from "../../../api/requests/company-requests";
import {
    convertBackendRatesToFrontend,
    convertFrontendRatesToBackend,
    buildEmptyRates,
    CARD_TYPES,
    MAX_INSTALLMENTS
} from "../utils/formatters";

/**
 * @param {string}  companyId
 * @param {boolean} canManage  Aktif POS oranlarını çekme / grup silme yetkisi
 *                             (SUPER_ADMIN veya COMPANY_ADMIN). Muhasebeci grup
 *                             oluşturup düzenleyebilir ama bu iki uca erişemez.
 */
export const useInstallmentGroups = (companyId, canManage = true) => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modals
    const [modalOpen, setModalOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deletingGroup, setDeletingGroup] = useState(null);

    // Hangi grubun abonelik anahtarı istek bekliyor — tüm liste yerine yalnız o
    // satırın anahtarı kilitlensin diye id tutuluyor.
    const [subscriptionLoadingId, setSubscriptionLoadingId] = useState(null);

    // Edit/Create State
    const [groupData, setGroupData] = useState({
        id: null,
        groupName: "",
        description: "",
        rates: {},
    });
    const [openCard, setOpenCard] = useState(CARD_TYPES[0]); // Akordeon kontrolü

    const loadGroups = async () => {
        if (!companyId) return;
        try {
            const json = await fetchGroups(companyId);
            setGroups(json.data || []);
        } catch (error) {
            toast.error("Taksit grupları yüklenemedi");
        }
    };

    useEffect(() => {
        loadGroups();
    }, [companyId]);

    // --- Modal İşlemleri ---
    const toggleModal = () => setModalOpen(!modalOpen);

    const openGroupModal = (group = null) => {
        if (group) {
            setGroupData({
                id: group.id,
                groupName: group.groupName,
                description: group.description || "",
                rates: convertBackendRatesToFrontend(group.installmentRates),
            });
        } else {
            // Izgara baştan tam kurulur (bkz. buildEmptyRates): boş bırakılan
            // markalarda hızlı işlemler ve "Tümünü Kaldır" ancak böyle çalışır.
            setGroupData({
                id: null,
                groupName: "",
                description: "",
                rates: buildEmptyRates(),
            });
        }
        setModalOpen(true);
    };

    // --- Aktif POS Oranlarını Çekme ---
    const handleFetchActivePosRates = async () => {
        if (!companyId) {
            toast.error("Firma seçili değil");
            return;
        }
        // Yetki yoksa uç 403 döner; modal her açıldığında hata bildirimi
        // göstermek yerine oran çekme adımı hiç denenmez.
        if (!canManage) return;

        try {
            setLoading(true);
            const json = await fetchActivePosByCompany(companyId);
            const { statusCode, data } = json || {};

            if (statusCode !== 200 || !data || !Array.isArray(data.banks)) {
                toast.error("Aktif POS oranları yüklenemedi");
                return;
            }

            /* Buton YALNIZ komisyon oranlarını tazeler; hangi taksitin satışa
               açık olduğuna dokunmaz. Eskiden ızgarayı sıfırdan kurup rates'i
               komple değiştiriyordu, bu yüzden kullanıcı taksitleri seçtikten
               sonra "Aktif POS oranlarını getir"e basınca hepsi geri açılıyordu.
               Butonun adı oran getirmek; seçim kullanıcınındır. */
            const brandInstallments = new Map(); // marka -> Map(taksit -> POS satırı)
            CARD_TYPES.forEach((card) => {
                // cardBrand eşleşen ilk bankayı al
                const bank = data.banks.find((b) => b.cardBrand === card);
                const byCount = new Map();
                (bank?.installments || []).forEach((inst) => {
                    byCount.set(Number(inst.installmentCount), inst);
                });
                brandInstallments.set(card, byCount);
            });

            let updatedCells = 0;

            setGroupData((prev) => {
                const nextRates = { ...(prev.rates || {}) };

                CARD_TYPES.forEach((card) => {
                    const current = prev.rates?.[card] || {};
                    const byCount = brandInstallments.get(card);
                    const updatedCounts = {};

                    for (let i = 1; i <= MAX_INSTALLMENTS; i++) {
                        const cell = current[i] || { id: null, value: "", enabled: defaultInstallmentEnabled(card) };
                        const pos = byCount.get(i);
                        const hasPosRate =
                            pos && pos.commissionPercent !== null && pos.commissionPercent !== undefined;

                        if (hasPosRate) updatedCells++;

                        updatedCounts[i] = {
                            ...cell,
                            // Oran POS'tan gelirse tazelenir, gelmezse mevcut değer korunur.
                            value: hasPosRate ? pos.commissionPercent : cell.value,
                            // enabled'a KASITLI olarak dokunulmuyor.
                            enabled: cell.enabled !== false,
                        };
                    }

                    nextRates[card] = updatedCounts;
                });

                return { ...prev, rates: nextRates };
            });

            toast.success(
                updatedCells > 0
                    ? `${updatedCells} komisyon oranı POS'tan güncellendi. Taksit seçimleriniz korundu.`
                    : "Aktif POS'ta güncellenecek komisyon oranı bulunamadı."
            );
        } catch (e) {
            console.error(e);
            toast.error("Aktif POS oranları alınırken hata oluştu");
        } finally {
            setLoading(false);
        }
    };

    // --- Tablo/Rate Değişimleri ---
    const handleRateChange = (card, inst, newVal) => {
        setGroupData((prev) => ({
            ...prev,
            rates: {
                ...prev.rates,
                [card]: {
                    ...(prev.rates?.[card] || {}),
                    [inst]: {
                        ...prev.rates?.[card]?.[inst],
                        value: newVal,
                    },
                },
            },
        }));
    };

    const handleEnabledChange = (card, inst, checked) => {
        setGroupData((prev) => ({
            ...prev,
            rates: {
                ...prev.rates,
                [card]: {
                    ...prev.rates[card],
                    [inst]: {
                        ...prev.rates[card]?.[inst],
                        enabled: checked,
                    },
                },
            },
        }));
    };

    /**
     * Bir markanın tüm taksitlerini açar/kapatır.
     *
     * 1..12'nin TAMAMI gezilir; eskiden yalnız state'te hâlihazırda bulunan
     * anahtarlar geziliyordu ve firmanın POS'unda olmayan markalarda buton
     * görünürde çalışıp hiçbir hücreyi değiştirmiyordu.
     */
    const handleToggleCard = (card, enabled) => {
        setGroupData((prev) => {
            const cardCounts = prev.rates?.[card] || {};
            const updatedCounts = {};
            for (let inst = 1; inst <= MAX_INSTALLMENTS; inst++) {
                updatedCounts[inst] = { ...(cardCounts[inst] || { id: null, value: "" }), enabled };
            }
            return {
                ...prev,
                rates: { ...prev.rates, [card]: updatedCounts }
            };
        });
    };

    /**
     * Hızlı işlem: taksidi ÜST SINIR olarak uygular — 1'den `inst`'e kadar hepsi
     * açılır, üstü kapanır. "6 Taksit" = tek çekim + 2 + 3 + 4 + 5 + 6.
     * Komisyon değerleri korunur; kapatmak oranı silmez, yalnız satışa kapatır.
     *
     * @param {number} inst  Üst sınır (1 = yalnız tek çekim)
     * @param {string} [card] Yalnız bu marka; verilmezse banka kartı (STANDART) dışındaki
     *                        tüm markalara uygulanır — banka kartı yalnız kendi satırından açılır.
     */
    const applyQuickInstallment = (inst, card = null) => {
        const targets = card ? [card] : CARD_TYPES.filter(defaultInstallmentEnabled);
        setGroupData((prev) => {
            const nextRates = { ...prev.rates };
            targets.forEach((brand) => {
                const cardCounts = prev.rates?.[brand] || {};
                const updatedCounts = {};
                for (let i = 1; i <= MAX_INSTALLMENTS; i++) {
                    updatedCounts[i] = {
                        ...(cardCounts[i] || { id: null, value: "" }),
                        enabled: i <= inst,
                    };
                }
                nextRates[brand] = updatedCounts;
            });
            return { ...prev, rates: nextRates };
        });
    };

    /** Bir markada o an açık olan taksit sayıları — çip durumunu boyamak için. */
    const getEnabledInstallments = (card) => {
        const cardCounts = groupData.rates?.[card] || {};
        const list = [];
        for (let i = 1; i <= MAX_INSTALLMENTS; i++) {
            if (cardCounts[i]?.enabled !== false) list.push(i);
        }
        return list;
    };

    const toggleCardAccordion = (card) => {
        setOpenCard((prev) => (prev === card ? null : card));
    };

    const handleSaveGroup = async () => {
        if (!groupData.groupName?.trim()) {
            toast.warning("Grup adı gerekli");
            return;
        }

        setLoading(true);
        try {
            const isEdit = Boolean(groupData.id);

            const dataToSend = {
                id: groupData.id,
                groupName: groupData.groupName.trim(),
                description: groupData.description?.trim() || "",
                companyId: companyId,
                installmentRates: convertFrontendRatesToBackend(groupData.rates),
            };

            await saveGroup(dataToSend, isEdit);

            toast.success(`Grup ${isEdit ? "güncellendi" : "oluşturuldu"}`);
            toggleModal();
            loadGroups();
        } catch (error) {
            toast.error(error.message || "Kaydetme hatası");
        } finally {
            setLoading(false);
        }
    };

    // --- Abonelik Taksit Grubu ---
    /**
     * Grubu paket (abonelik) ödemesinin taksit grubu yapar ya da bu işareti kaldırır.
     *
     * Sistem genelinde tek grup açık olabilir: bir grubu açmak öncekini backend'de
     * otomatik kapatır, bu yüzden iki ayrı çağrı yapılmaz. Liste tek çağrıda iki
     * satırı birden değiştiğinden optimistic güncelleme yerine baştan yükleniyor.
     *
     * @param {object}  group   İşaretlenecek/kaldırılacak grup
     * @param {boolean} enabled true = bu grup kullanılsın, false = tamamen kapat
     */
    const toggleSubscriptionGroup = async (group, enabled) => {
        if (!group?.id) return;

        setSubscriptionLoadingId(group.id);
        try {
            const json = await setSubscriptionGroup(group.id, enabled);

            if (json?.statusCode !== 200) {
                toast.error(json?.message || "Abonelik taksit grubu güncellenemedi");
                return;
            }

            toast.success(
                enabled
                    ? `"${group.groupName}" paket ödemelerinde kullanılacak`
                    : "Paket ödemelerinde taksit kapatıldı"
            );
            await loadGroups();
        } catch (e) {
            console.error(e);
            toast.error("Abonelik taksit grubu güncellenirken hata oluştu");
        } finally {
            setSubscriptionLoadingId(null);
        }
    };

    // --- Silme İşlemleri ---
    /** @param {object} group Silinecek grup (onay modalı adını ve cari sayısını gösterir) */
    const handleDeleteClick = (group) => {
        setDeletingGroup(group);
        setConfirmModalOpen(true);
    };

    const confirmGroupDelete = async () => {
        if (!canManage) {
            toast.error("Taksit grubu silme yetkiniz yok.");
            setConfirmModalOpen(false);
            return;
        }
        setLoading(true);
        try {
            const res = await deleteGroup(deletingGroup?.id);
            if (res.ok) {
                toast.success("Grup silindi");
                loadGroups();
            } else {
                toast.error("Silinemedi");
            }
        } catch {
            toast.error("Silme hatası");
        } finally {
            setLoading(false);
            setConfirmModalOpen(false);
            setDeletingGroup(null);
        }
    };

    return {
        groups,
        loading,
        modalOpen,
        toggleModal,
        openGroupModal,
        groupData,
        setGroupData,
        openCard,
        toggleCardAccordion,
        handleFetchActivePosRates,
        handleRateChange,
        handleEnabledChange,
        handleToggleCard,
        applyQuickInstallment,
        getEnabledInstallments,
        handleSaveGroup,
        handleDeleteClick,
        confirmDelete: confirmGroupDelete,
        confirmModalOpen,
        setConfirmModalOpen,
        deletingGroup,
        toggleSubscriptionGroup,
        subscriptionLoadingId,
        companyId,
        canManage
    };
};